import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Form, Row, Col } from 'react-bootstrap';
import {
  ControlledDateInput,
  Form as CustomForm,
} from '../../../../../../../components/form7';
import { FormGroup } from '../../../../../../../components/form/FormGroup';
import { toDateOnlyString } from '../../../../../../../utils/dateUtils';
import { t as rootT } from 'i18next';
import InputLabel from '../../../../../../../components/form/InputLabel';

const EffectiveDateWatcher: React.FC<
  React.PropsWithChildren<{
    onEffectiveDateChange: (value: string | null) => void;
    disabled: boolean;
  }>
> = ({ onEffectiveDateChange, disabled }) => {
  const { watch } = useFormContext();
  const effectiveDate = watch('effectiveDate');

  useEffect(() => {
    if (!disabled) {
      const dateValue = toDateOnlyString(effectiveDate);
      onEffectiveDateChange(dateValue);
    }
  }, [effectiveDate, onEffectiveDateChange, disabled]);

  return null;
};

const EndDateWatcher: React.FC<
  React.PropsWithChildren<{
    onEndDateChange: (value: string | null) => void;
    disabled: boolean;
  }>
> = ({ onEndDateChange, disabled }) => {
  const { watch } = useFormContext();
  const endDate = watch('endDate');

  useEffect(() => {
    if (!disabled) {
      const dateValue = toDateOnlyString(endDate);
      onEndDateChange(dateValue);
    }
  }, [endDate, onEndDateChange, disabled]);

  return null;
};

type DateSectionProps = {
  effectiveDate: string;
  endDate: string;
  unknownEndDate: boolean;
  syncDatesWithPosition: boolean;
  positionChangeData?: {
    effectiveDate?: string;
    unknownEndDate?: boolean;
    endDate?: string;
  };
  validationErrors: {
    effectiveDateError: string | null;
    endDateError: string | null;
  };
  onEffectiveDateChange: (value: string | null) => void;
  onEndDateChange: (value: string | null) => void;
  onUnknownEndDateChange: (value: boolean) => void;
  onSyncToggle: (value: boolean) => void;
  t: (key: string, options?: { defaultValue?: string }) => string;
};

export const DateSection: React.FC<
  React.PropsWithChildren<DateSectionProps>
> = ({
  effectiveDate,
  endDate,
  unknownEndDate,
  syncDatesWithPosition,
  positionChangeData,
  validationErrors,
  onEffectiveDateChange,
  onEndDateChange,
  onUnknownEndDateChange,
  onSyncToggle,
  t,
}) => {
  const displayEffectiveDate =
    syncDatesWithPosition && positionChangeData?.effectiveDate
      ? positionChangeData.effectiveDate
      : effectiveDate;
  const displayUnknownEndDate =
    syncDatesWithPosition && positionChangeData?.unknownEndDate !== undefined
      ? positionChangeData.unknownEndDate
      : unknownEndDate;
  const displayEndDate =
    syncDatesWithPosition && positionChangeData?.endDate
      ? positionChangeData.endDate
      : endDate;

  return (
    <>
      {positionChangeData && (
        <Form.Check
          type="switch"
          id="sync-dates-with-position"
          label={t('syncDatesWithPosition')}
          checked={syncDatesWithPosition}
          onChange={(e) => onSyncToggle(e.target.checked)}
          className="mb-2"
        />
      )}

      <Row>
        <Col md={6}>
          <CustomForm
            key={`effectiveDate-sync-${syncDatesWithPosition ? 'on' : 'off'}`}
            onSubmit={() => {}}
            defaultValues={{ effectiveDate: displayEffectiveDate }}
            values={{ effectiveDate: displayEffectiveDate }}
            shouldUnregister={true}>
            <EffectiveDateWatcher
              onEffectiveDateChange={onEffectiveDateChange}
              disabled={syncDatesWithPosition}
            />
            <FormGroup>
              <InputLabel label={t('effectiveDate')} required={true} />
              <ControlledDateInput
                name="effectiveDate"
                type="date"
                disabled={syncDatesWithPosition}
                options={{
                  dateFormat: 'Y-m-d',
                  altInput: true,
                  altFormat: rootT('flatpickr.dateFormat'),
                }}
              />
              {validationErrors.effectiveDateError && (
                <Form.Control.Feedback
                  type="invalid"
                  style={{ display: 'block' }}>
                  {validationErrors.effectiveDateError}
                </Form.Control.Feedback>
              )}
            </FormGroup>
          </CustomForm>
        </Col>

        {!displayUnknownEndDate && (
          <Col md={6}>
            <CustomForm
              key={`endDate-sync-${syncDatesWithPosition ? 'on' : 'off'}`}
              onSubmit={() => {}}
              defaultValues={{ endDate: displayEndDate }}
              values={{ endDate: displayEndDate }}
              shouldUnregister={true}>
              <EndDateWatcher
                onEndDateChange={onEndDateChange}
                disabled={syncDatesWithPosition}
              />
              <FormGroup>
                <InputLabel label={t('endDate')} required={false} />
                <ControlledDateInput
                  name="endDate"
                  type="date"
                  disabled={syncDatesWithPosition}
                  options={{
                    dateFormat: 'Y-m-d',
                    altInput: true,
                    altFormat: rootT('flatpickr.dateFormat'),
                  }}
                />
                {validationErrors.endDateError && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: 'block' }}>
                    {validationErrors.endDateError}
                  </Form.Control.Feedback>
                )}
              </FormGroup>
            </CustomForm>
          </Col>
        )}
      </Row>

      <Form.Check
        type="switch"
        id="unknown-end-date"
        label={t('noEndDate')}
        checked={displayUnknownEndDate}
        onChange={(e) => onUnknownEndDateChange(e.target.checked)}
        className="mb-3"
        disabled={syncDatesWithPosition}
      />
    </>
  );
};
